import styled from '@emotion/styled';
import { IconButton, Menu, MenuItem, Typography, useMediaQuery } from '@material-ui/core';
import MoreVert from '@material-ui/icons/MoreVert';
import { createElement, FC, Fragment, memo, MouseEvent, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { CreativeFormatCreativeType } from '../../../../generated/graphql';
import theme from '../../../../theme';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import mq from '../../../styleUtility/mediaquery';
import { InputtedCreativeFormat } from '../../../types/InputtedCreativeFormat';
import _CreativeFormatCreativeTypeIcon from '../CreativeFormatCreativeTypeIcon';
import _CreativeFormatDevice from '../CreativeFormatDevice';
import CreativeFormatNetworkIcon from '../CreativeFormatNetworkIcon';
import CreativeFormatPlacementLabel from '../CreativeFormatPlacementLabel';
import EditAspectRatioDialog from './EditAspectRatioDialog';

type Props = {
  creativeFormat: InputtedCreativeFormat;
  onClickDeleteButton: ({ creativeFormatId }: { creativeFormatId: string }) => void;
  onSubmitEditingAspectRatio: ({
    creativeFormatId,
    width,
    height,
  }: {
    creativeFormatId: string;
    width: number;
    height: number;
  }) => void;
  className?: string;
};

const CreativeFormatResizeItem: FC<Props> = ({
  creativeFormat,
  onClickDeleteButton,
  onSubmitEditingAspectRatio,
  className,
}) => {
  const isPC = useMediaQuery(`(min-width:${theme.breakpoints.values['lg']}px)`);
  // AspectRatio編集モーダルのフラグ
  const [modalOpen, setModalOpen] = useState(false);

  const isAspectRatioVisible =
    creativeFormat.attribute.aspectRatio &&
    creativeFormat.attribute.aspectRatio.aspectRatio.width * creativeFormat.attribute.aspectRatio.aspectRatio.height >
      0;

  // サブメニューのフラグ
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  // 各イベント
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChangeAspectRatio = () => {
    setAnchorEl(null);
    setModalOpen(true);
  };

  const handleDeleteItem = () => {
    setAnchorEl(null);
    onClickDeleteButton({
      creativeFormatId: creativeFormat.tmpid! || creativeFormat.id!,
    });
  };

  const handleSubmitAspectRatio = ({ width, height }: { width: number; height: number }) => {
    onSubmitEditingAspectRatio({
      creativeFormatId: creativeFormat.tmpid! || creativeFormat.id!,
      width,
      height,
    });
    setModalOpen(false);
  };

  return (
    <>
      {isPC ? (
        <Root className={className}>
          <CreativeFormatCreativeTypeIcon creativeType={creativeFormat.creativeType as CreativeFormatCreativeType} />
          <Device>
            <CreativeFormatDevice
              network={creativeFormat.network}
              aspectRatio={
                creativeFormat.attribute.aspectRatio ? creativeFormat.attribute.aspectRatio.aspectRatio : null
              }
            />
          </Device>
          <FormatByNetworkAndPlacement>
            <NetworkIcon network={creativeFormat.network} size={24} />
            <PlacementName>
              <Typography variant="subtitle1">
                <CreativeFormatPlacementLabel placement={creativeFormat.placement} />
              </Typography>
            </PlacementName>
          </FormatByNetworkAndPlacement>
          <FormatByAttribute>
            {creativeFormat.attribute.aspectRatio ? (
              <Typography variant="body2">
                {creativeFormat.attribute.aspectRatio.aspectRatio.width}:
                {creativeFormat.attribute.aspectRatio.aspectRatio.height}（
                {creativeFormat.attribute.aspectRatio.size.width}:{creativeFormat.attribute.aspectRatio.size.height}）
              </Typography>
            ) : null}
            {creativeFormat.attribute.playbackTime ? (
              <Typography variant="body2">
                <FormattedMessage
                  id="creative_format.playback_time"
                  values={{
                    seconds: creativeFormat.attribute.playbackTime.seconds,
                  }}
                />
              </Typography>
            ) : null}
          </FormatByAttribute>
          <Actions>
            <IconButton aria-label="more" aria-controls="long-menu" aria-haspopup="true" onClick={handleClick}>
              <MoreVert />
            </IconButton>
            <Menu anchorEl={anchorEl} keepMounted open={menuOpen} onClose={handleClose}>
              {isAspectRatioVisible ? (
                <MenuItem onClick={handleChangeAspectRatio}>
                  <Typography variant="body2">
                    <FormattedMessage id="creative_format_editor.creative_format_menu.aspect_ratio" />
                  </Typography>
                </MenuItem>
              ) : null}
              <MenuItem onClick={handleDeleteItem}>
                <Typography variant="body2" color="error">
                  <FormattedMessage id="creative_format_editor.creative_format_menu.delete_item" />
                </Typography>
              </MenuItem>
            </Menu>
          </Actions>
          {isAspectRatioVisible ? (
            <EditAspectRatioDialog
              aspectRatio={creativeFormat.attribute.aspectRatio!.aspectRatio}
              displaySize={creativeFormat.attribute.aspectRatio!.size}
              isOpen={modalOpen}
              onSubmit={handleSubmitAspectRatio}
              onRequestClose={(_: any) => {
                setModalOpen(false);
              }}
            />
          ) : null}
        </Root>
      ) : (
        <RootSp className={className}>
          <FlexLeft>
            <CreativeFormatCreativeTypeIcon creativeType={creativeFormat.creativeType as CreativeFormatCreativeType} />
          </FlexLeft>
          <FlexRight>
            <FormatByNetworkAndPlacement>
              <NetworkIcon network={creativeFormat.network} size={24} />
              <PlacementName>
                <Typography variant="subtitle1">
                  <CreativeFormatPlacementLabel placement={creativeFormat.placement} />
                </Typography>
              </PlacementName>
            </FormatByNetworkAndPlacement>
            <FormatByAttribute>
              {creativeFormat.attribute.aspectRatio ? (
                <Typography variant="body2">
                  {creativeFormat.attribute.aspectRatio.aspectRatio.width}:
                  {creativeFormat.attribute.aspectRatio.aspectRatio.height}（
                  {creativeFormat.attribute.aspectRatio.size.width}:{creativeFormat.attribute.aspectRatio.size.height}）
                </Typography>
              ) : null}
              {creativeFormat.attribute.playbackTime ? (
                <Typography variant="body2">
                  <FormattedMessage
                    id="creative_format.playback_time"
                    values={{
                      seconds: creativeFormat.attribute.playbackTime.seconds,
                    }}
                  />
                </Typography>
              ) : null}
            </FormatByAttribute>
          </FlexRight>
          <Actions>
            <IconButton aria-label="more" aria-controls="long-menu" aria-haspopup="true" onClick={handleClick}>
              <MoreVert />
            </IconButton>
            <Menu anchorEl={anchorEl} keepMounted open={menuOpen} onClose={handleClose}>
              {isAspectRatioVisible ? (
                <MenuItem onClick={handleChangeAspectRatio}>
                  <Typography variant="body2">
                    <FormattedMessage id="creative_format_editor.creative_format_menu.aspect_ratio" />
                  </Typography>
                </MenuItem>
              ) : null}
              <MenuItem onClick={handleDeleteItem}>
                <Typography variant="body2" color="error">
                  <FormattedMessage id="creative_format_editor.creative_format_menu.delete_item" />
                </Typography>
              </MenuItem>
            </Menu>
          </Actions>
          {isAspectRatioVisible ? (
            <EditAspectRatioDialog
              aspectRatio={creativeFormat.attribute.aspectRatio!.aspectRatio}
              displaySize={creativeFormat.attribute.aspectRatio!.size}
              isOpen={modalOpen}
              onSubmit={handleSubmitAspectRatio}
              onRequestClose={(_: any) => {
                setModalOpen(false);
              }}
            />
          ) : null}
        </RootSp>
      )}
    </>
  );
};

const Root = styled('div')(
  gridLayoutContainer({
    columns: ['50px', '50px', 'auto', '145px', '24px'],
    rows: ['104px'],
    areas: [['device', 'icon', 'placement', 'attribute', 'action']],
    alignItems: 'center',
    justifyItems: 'flex-start',
  }),
  {
    position: 'relative',
    padding: SPACING_REGULAR,
  },
);

const RootSp = styled('div')({
  position: 'relative',
  display: 'flex',
  justifyContent: 'flex-start',
  height: '100%',
  alignItems: 'center',
  padding: '16px 0',
});

const FlexLeft = styled('div')({
  display: 'flex',
});

const FlexRight = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: '20px',
});

const Device = styled('div')(gridLayoutItem('device'), {});

const CreativeFormatDevice = styled(_CreativeFormatDevice)({
  width: '100%',
});

const CreativeFormatCreativeTypeIcon = styled(_CreativeFormatCreativeTypeIcon)(gridLayoutItem('icon'), {
  marginLeft: SPACING_REGULAR,
  [mq('sp')]: {
    marginLeft: 0,
  },
});

const FormatByNetworkAndPlacement = styled('div')(
  gridLayoutItem('placement'),
  flexibleLayoutContainer({
    alignItems: 'center',
    justifyContent: 'center',
  }),
  {
    marginLeft: SPACING_REGULAR,
    [mq('sp')]: {
      marginLeft: 0,
      justifyContent: 'flex-start',
    },
  },
);

const NetworkIcon = styled(CreativeFormatNetworkIcon)(gridLayoutItem('network'));

const PlacementName = styled('div')(gridLayoutItem('placement'), {
  width: '80%',
  wordBreak: 'break-all',
  marginLeft: SPACING_UNIT,
});

const FormatByAttribute = styled('div')(gridLayoutItem('attribute'), {
  paddingLeft: SPACING_REGULAR,
  [mq('sp')]: {
    display: 'flex',
    justifyContent: 'flex-start',
    paddingLeft: 0,
    marginTop: '8px',
  },
});

const Actions = styled('div')({
  position: 'absolute',
  top: SPACING_UNIT,
  right: 0,
});

export default memo(CreativeFormatResizeItem);
